import { Button, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import { FieldPath, useFormContext } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useCreateProduct, usePatchProduct } from "../../../../api/products";
import useFormImageUpload from "../../../../hooks/formHooks/useFormImageUpload";
import useNotify from "../../../../hooks/useNotify";
import { PRODUCT_CATEGORY, ProductRequest } from "../../../../models/Product";
import { requiredForSave } from "../../../../pages/reward-inventory-portal/create-product/CreateProduct.const";
import {
  rewardInventoryPortalPaths,
  rootPaths,
} from "../../../../routes/routePaths";
import { MODE } from "../../../../stores/formStore";

const messageErrorRequired = {
  message: "This field is required",
  type: "required",
};

const pointsFromLessThanPointsTo = {
  message: "Points from A to B, B must greater than A.",
  type: "required",
};

const endDateSmallerThanStartDate = {
  message: "End date must greater than start date",
  type: "required",
};

const ButtonSave = ({ productId }: { productId: string }) => {
  const formContext = useFormContext<ProductRequest>();
  const { mutateAsync: createProductAsync } = useCreateProduct();
  const { mutateAsync: patchProductAsync } = usePatchProduct();
  const { setNotifySuccess, setNotifyError } = useNotify();
  const [isLoading, setisLoading] = useState(false);
  const navigate = useNavigate();
  const handleImageUpload = useFormImageUpload();

  const handleClick = async () => {
    try {
      const incompleteFields = requiredForSave.reduce(
        (acc: FieldPath<ProductRequest>[], field) => {
          const value = formContext.getValues(field);
          if (!value?.toString().length) {
            acc.push(field);
          }
          return acc;
        },
        [],
      );

      if (
        formContext.getValues("productDetails.pointsFrom") &&
        formContext.getValues("productDetails.pointsTo")
      ) {
        const value1 = formContext.getValues("productDetails.pointsFrom");
        const value2 = formContext.getValues("productDetails.pointsTo");
        if (value1 && value2 && value1 >= value2) {
          formContext.setError(
            "productDetails.pointsTo",
            pointsFromLessThanPointsTo,
          );
        }
      }

      if (formContext.getValues("productDetails.isOnDemand")) {
        const value = formContext.getValues("productDetails.externalProductId");
        if (!value?.toString().length) {
          incompleteFields.push("productDetails.externalProductId");
        }
      } else {
        formContext.clearErrors("productDetails.externalProductId");
      }

      if (
        formContext.getValues("productDetails.userLimitFrequency") &&
        !formContext.getValues("productDetails.userLimitNumber")
      ) {
        formContext.setError(
          "productDetails.userLimitNumber",
          messageErrorRequired,
        );
      }

      if (
        formContext.getValues("productDetails.voucherLimitFrequency") &&
        !formContext.getValues("productDetails.voucherLimitNumber")
      ) {
        formContext.setError(
          "productDetails.voucherLimitNumber",
          messageErrorRequired,
        );
      }

      if (
        formContext.getValues("productDetails.productCategory") ===
          PRODUCT_CATEGORY.AUCTION &&
        !formContext.getValues("productDetails.auctionType")
      ) {
        formContext.setError(
          "productDetails.auctionType",
          messageErrorRequired,
        );
      } else {
        formContext.clearErrors("productDetails.auctionType");
      }

      if (formContext.getValues("productDetails.auctionType")) {
        if (!formContext.getValues("productDetails.startDate")) {
          formContext.setError(
            "productDetails.startDate",
            messageErrorRequired,
          );
        } else {
          formContext.clearErrors("productDetails.startDate");
        }
        if (!formContext.getValues("productDetails.expirationDate")) {
          formContext.setError(
            "productDetails.expirationDate",
            messageErrorRequired,
          );
        } else {
          formContext.clearErrors("productDetails.expirationDate");
        }
        if (
          formContext.getValues("productDetails.startDate") &&
          formContext.getValues("productDetails.expirationDate") &&
          dayjs(
            formContext.getValues("productDetails.expirationDate"),
            "DD/MM/YYYY",
          ).valueOf() <
            dayjs(
              formContext.getValues("productDetails.startDate"),
              "DD/MM/YYYY",
            ).valueOf()
        ) {
          formContext.setError(
            "productDetails.expirationDate",
            endDateSmallerThanStartDate,
          );
        }
      } else {
        formContext.clearErrors("productDetails.startDate");
        formContext.clearErrors("productDetails.expirationDate");
      }

      if (
        incompleteFields.length ||
        formContext.formState.errors.productDetails
      ) {
        // console.log("----------- incompleteFields", JSON.stringify(incompleteFields));
        // console.log(
        //   "----------- formContext.formState.errors.productDetails",
        //   JSON.stringify(formContext.formState.errors.productDetails),
        // );
        incompleteFields.forEach(fieldPath => {
          formContext.setError(fieldPath, messageErrorRequired);
        });

        throw new Error("Required fields must be completed to save");
      }

      setisLoading(true);
      if (handleImageUpload) {
        await handleImageUpload();
      }

      const productRequest = formContext.getValues();

      const productRequestConvert = {
        ...productRequest,
        productDetails: {
          ...productRequest.productDetails,
          userLimitFrequency:
            productRequest.productDetails.userLimitFrequency || undefined,
          userLimitNumber:
            productRequest.productDetails.userLimitNumber || undefined,
          voucherLimitFrequency:
            productRequest.productDetails.voucherLimitFrequency || undefined,
          voucherLimitNumber:
            productRequest.productDetails.voucherLimitNumber || undefined,
        },
      };

      if (!productRequestConvert.productDetails.auctionType) {
        delete productRequestConvert.productDetails.auctionType;
      }

      const response = productId
        ? await patchProductAsync({
            productId,
            productRequest: productRequestConvert,
          })
        : await createProductAsync({ productRequest: productRequestConvert });
      setNotifySuccess("Product Saved");
      navigate(
        `${
          rootPaths.rewardInventoryPortal
        }/${rewardInventoryPortalPaths.product.replace(
          ":productId",
          response.productId,
        )}`,
        { state: { mode: MODE.EDIT } },
      );
    } catch (error) {
      // Need to notify the UI
      if (error instanceof Error) {
        setNotifyError(error.message);
      } else {
        setNotifyError("Oops! something went wrong.");
      }
    }
    setisLoading(false);
  };

  return (
    <Button
      onClick={handleClick}
      variant="secondary"
      size="small"
      sx={{ width: 80 }}
      disabled={isLoading}
      startIcon={isLoading && <CircularProgress size={20} />}
    >
      Save
    </Button>
  );
};

export default ButtonSave;
